/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, Input } from '@chakra-ui/react';

export interface MappingSuggestion {
  value: string;
  label?: string;
  category?: string;
  description?: string;
}

export interface MappingAutocompleteInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minChars?: number;
  suggestions?: MappingSuggestion[];
  getSuggestions?: (query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  bg?: string;
  /**
   * For a box that commits a draft rather than every keystroke — the ORTB body's rename (Todd,
   * 2026-09-28). Optional: every other caller commits as it types, and absence is that.
   */
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  fontFamily?: string;
}

export const MappingAutocompleteInput: React.FC<MappingAutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  minChars = 1,
  suggestions,
  getSuggestions,
  size = 'sm',
  bg = 'white',
  onBlur,
  onKeyDown,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  fontFamily,
}) => {
  const listId = React.useId();
  const [items, setItems] = React.useState<MappingSuggestion[]>(suggestions ?? []);

  React.useEffect(() => {
    setItems(suggestions ?? []);
  }, [suggestions]);

  React.useEffect(() => {
    let active = true;
    const run = async () => {
      if (!getSuggestions) return;
      if ((value ?? '').trim().length < minChars) {
        setItems(suggestions ?? []);
        return;
      }
      const result = await getSuggestions(value);
      if (active) {
        setItems(result ?? []);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [getSuggestions, minChars, suggestions, value]);

  return (
    <Box>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        fontFamily={fontFamily}
        placeholder={placeholder}
        disabled={disabled}
        list={listId}
        size={size}
        bg={bg}
        // A refused value is blocking, and blocking wears the red border (`#1456`).
        borderColor={ariaInvalid ? 'error.border' : 'border.default'}
        _focus={{ borderColor: 'accent.solid', boxShadow: '0 0 0 1px var(--chakra-colors-accent-solid)' }}
      />
      <datalist id={listId}>
        {items.map((item) => (
          <option key={`${item.value}-${item.category ?? ''}`} value={item.value}>
            {item.label ?? item.value}
          </option>
        ))}
      </datalist>
    </Box>
  );
};
